import type { SkillFileNode } from '@onefootprint/request-types/dashboard';
import type { Dispatch, RefObject } from 'react';
import type { Action, State } from '../store/reducer';
import {
  addFileToFolder,
  addFolderToRoot,
  dropExternalNodes,
  moveNodeTo,
  removeNodeAt,
  renameNodeAt,
  type SkillsOpResult,
  setFileContentAt,
} from '../store/skills-operations';
import type { DropPosition } from '../store/skills-path';
import type { MutateOp } from './use-builder-mutation';

type SkillsActions = {
  addFolder: () => void;
  addFile: (parentPath: string) => void;
  remove: (path: string) => void;
  rename: (path: string, name: string) => void;
  setContent: (path: string, content: string) => void;
  move: (fromPath: string, toPath: string, position: DropPosition) => void;
  dropExternal: (targetPath: string | null, position: DropPosition | 'root', nodes: SkillFileNode[]) => void;
  select: (path: string | null) => void;
};

type Args = {
  stateRef: RefObject<State>;
  dispatch: Dispatch<Action>;
  mutateOp: MutateOp;
};

const buildSkillsActions = ({ stateRef, dispatch, mutateOp }: Args): SkillsActions => {
  const applyAndSave = (result: SkillsOpResult) => {
    dispatch({ type: 'updateSkills', skills: result.skills });
    if (result.selectedPath !== undefined) {
      dispatch({ type: 'setSelectedSkillPath', path: result.selectedPath });
    }
    mutateOp({ kind: 'update_skills', skills: result.skills });
  };

  const applyLocal = (result: SkillsOpResult) => {
    dispatch({ type: 'updateSkills', skills: result.skills });
    if (result.selectedPath !== undefined) {
      dispatch({ type: 'setSelectedSkillPath', path: result.selectedPath });
    }
  };

  return {
    addFolder: () => {
      applyAndSave(addFolderToRoot(stateRef.current.bundle.skills));
    },

    addFile: parentPath => {
      const result = addFileToFolder(stateRef.current.bundle.skills, parentPath);
      if (result) applyAndSave(result);
    },

    remove: path => {
      applyAndSave(removeNodeAt(stateRef.current.bundle.skills, path, stateRef.current.selectedSkillPath));
    },

    rename: (path, name) => {
      applyLocal(renameNodeAt(stateRef.current.bundle.skills, path, name));
    },

    setContent: (path, content) => {
      applyLocal(setFileContentAt(stateRef.current.bundle.skills, path, content));
    },

    move: (fromPath, toPath, position) => {
      const result = moveNodeTo(stateRef.current.bundle.skills, fromPath, toPath, position);
      if (result.skills !== stateRef.current.bundle.skills) applyAndSave(result);
    },

    dropExternal: (targetPath, position, nodes) => {
      applyAndSave(
        dropExternalNodes(stateRef.current.bundle.skills, {
          targetPath,
          position,
          nodes,
          currentSelectedPath: stateRef.current.selectedSkillPath,
        }),
      );
    },

    select: path => {
      dispatch({ type: 'setSelectedSkillPath', path });
    },
  };
};

export type { SkillsActions };
export default buildSkillsActions;
